import type { InnspelStorage } from '@innspel/core';

/**
 * The offline queue on the web lives in `localStorage` (tech-decisions.md, «M1.5»). The
 * only thing stored is the queue (DD-41): at most 30 texts, never a screenshot,
 * never the feedback token and never an install or device ID (K20). The token
 * lives in `sessionStorage` in the portal and only in memory in the package.
 *
 * This file is the ONE allowed to touch `localStorage` — the lint guard in
 * eslint.config.js enforces that for the rest of `src/`.
 *
 * `localStorage` can throw (private mode in older Safari, blocked storage,
 * quota). The adapter then falls back to memory for the rest of the session: the text
 * is never lost silently, it just waits for a shorter time.
 */
export function createStorage(lagring: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = nettleserLagring()): InnspelStorage {
  const minne = new Map<string, string>();
  const trygg = <T>(f: () => T, ellers: () => T): T => {
    try {
      return f();
    } catch {
      return ellers();
    }
  };
  return {
    get: async (key) => trygg(() => lagring?.getItem(key) ?? minne.get(key) ?? null, () => minne.get(key) ?? null),
    set: async (key, value) => {
      minne.set(key, value);
      trygg(() => lagring?.setItem(key, value), () => undefined);
    },
    remove: async (key) => {
      minne.delete(key);
      trygg(() => lagring?.removeItem(key), () => undefined);
    },
  };
}

/** In-memory storage — for tests and for hosts that do not want the queue to outlive the tab. */
export function createMemoryStorage(): InnspelStorage {
  return createStorage(null);
}

function nettleserLagring(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
